import React, { useCallback, useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import styled from '@emotion/styled';
import { Button, Icon, Menu, message, Modal } from 'antd';
import { Trans, useTranslation } from 'react-i18next';
import OrganisationApp from '../../../../../store/types/organisation-app';
import UniversalDevice from '../../../../../store/types/universal-device';
import DevicesUniversalList from '../../../../common/universal-devices/devices-universal-list/devices-universal-list.component';
import EmptyDeviceView from './empty-device-universal-view/empty-device-universal-view.component';
import ConnectSetupDeviceUniversalModal from './connect-setup-device-universal-modal/connect-setup-device-universal-modal.component';
import Environment from '../../../../../store/types/environment';
import { DeviceCreateParams } from '../../../../../store/models/app-devices/app-devices.model';
import { UniversalDeviceConnectParams } from '../../../../../store/models/app-devices-universal/app-devices-universal.model';
import DeviceTypeEnum from '../../../../../store/types/device-type.enum';
import SearchBar, {
  SearchBarWrap,
} from '../../../../common/search-bar/search-bar.component';
import useLayoutView from '../../../../common/layout-view/use-layout-view';
import ContentWrap from '../../../../common/app-layout/content/content-wrap.component';

interface DevicesProps
  extends RouteComponentProps<{ appId: string; organisationId: string }> {
  devices: UniversalDevice[];
  loaded: boolean;
  fetchDevices: (params: { appName: string; silent?: boolean }) => void;
  lastUpdated?: Date;
  app: OrganisationApp | null;
  canCreate: boolean;
  createDevice: (params: DeviceCreateParams) => Promise<void>;
  fetchEnvironments: (params: { organizationId: string }) => void;
  environments: Environment[];
  disconnectDevice: (params: UniversalDeviceConnectParams) => Promise<void>;
}

const DevicesUniversal = (props: DevicesProps) => {
  const {
    devices,
    loaded,
    fetchDevices,
    lastUpdated,
    app,
    canCreate,
    fetchEnvironments,
    match: {
      params: { organisationId },
    },
    disconnectDevice,
  } = props;

  const [isConnectOrSetupModalVisible, setIsConnectOrSetupModalVisible] = useState(false);
  const [query, setQuery] = useState<string>('');

  const { t } = useTranslation();
  const { layoutView, setLayoutView } = useLayoutView('grid');

  const handleConnectOrSetupModalOpen = useCallback(() => {
    setIsConnectOrSetupModalVisible(true);
  }, []);

  const handleConnectOrSetupModalClose = useCallback(() => {
    setIsConnectOrSetupModalVisible(false);
  }, []);

  const fetchAppDevices = useCallback(
    async ({ silent }: { silent: boolean; page: number; pageSize: number }) => {
      if (app) {
        fetchDevices({ appName: app.appName, silent });
      }
    },
    [app, fetchDevices],
  );

  useEffect(() => {
    fetchEnvironments({ organizationId: organisationId });
  }, [fetchEnvironments, organisationId]);

  const handleDeviceDisconnectClick = useCallback(
    async (deviceId: string) => {
      const device = devices.find((dev) => dev.id === deviceId);

      if (app && device) {
        Modal.confirm({
          title: t('confirmAction'),
          content: (
            <Trans
              i18nKey="areYouSureYouWantToDisconnectDevice"
              values={{
                deviceName: device.deviceName,
                appName: app.appName,
              }}
              components={{ strong: <strong /> }}
            />
          ),
          width: '40%',
          onOk: async () => {
            try {
              await disconnectDevice({
                appId: app.id,
                deviceId,
                deviceType: 'iot',
              });
              message.success(t('successfullyDisconnectedDevice'));
            } catch (err) {
              message.error(t('failedToDisconnectDevice'));
            }
          },
          onCancel: () => {},
        });
      }
    },
    [app, devices, disconnectDevice, t],
  );

  const renderIotExtraActions = useCallback(
    (deviceId: string) => (
      <Menu>
        <Menu.Item
          onClick={({ domEvent }) => {
            domEvent.preventDefault();
            return handleDeviceDisconnectClick(deviceId);
          }}
          key="disconnect"
        >
          <span>
            <Icon type="disconnect" />
            <span>{t('disconnectDevice')}</span>
          </span>
        </Menu.Item>
      </Menu>
    ),
    [handleDeviceDisconnectClick, t],
  );

  const handleSearch = useCallback((value: string): void => {
    setQuery(value.trim());
  }, []);

  if (!app) {
    return null;
  }

  const renderExtraActions =
    app.deviceType === DeviceTypeEnum.IOT ? renderIotExtraActions : undefined;

  return (
    <>
      {loaded && !!devices.length && (
        <SearchBarWrap>
          <SearchBar
            searchInputProps={{
              placeholder: t('searchDevicePlaceholderLabel'),
              onSearch: handleSearch,
            }}
            layoutViewProps={{
              defaultLayout: 'grid',
              onChange: setLayoutView,
            }}
            extra={
              canCreate && (
                <ConnectDeviceButton
                  size="large"
                  icon="api"
                  onClick={handleConnectOrSetupModalOpen}
                >
                  {t('connectDevice')}
                </ConnectDeviceButton>
              )
            }
          />
        </SearchBarWrap>
      )}
      <ContentWrap>
        {loaded && !devices.length && (
          <EmptyDeviceView
            app={app}
            onConnectDevice={handleConnectOrSetupModalOpen}
            canCreate={canCreate}
          />
        )}
        {((loaded && devices.length) || !loaded) && (
          <DevicesUniversalList
            error={undefined}
            isPolling={!isConnectOrSetupModalVisible}
            lastUpdated={lastUpdated}
            devices={devices}
            loaded={loaded}
            fetchDevices={fetchAppDevices}
            defaultShowAll
            renderExtraActions={renderExtraActions}
            customQuery={query}
            layout={layoutView}
            // Workaround for local pagination
            customPageSize={500}
            customPageSizeOption={[500]}
          />
        )}
        <ConnectSetupDeviceUniversalModal
          {...props}
          isVisible={isConnectOrSetupModalVisible}
          onClose={handleConnectOrSetupModalClose}
          app={app}
        />
      </ContentWrap>
    </>
  );
};

const ConnectDeviceButton = styled(Button)`
  height: inherit;
` as any;

export default DevicesUniversal;
